import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useAlerts } from 'src/common/AlertManager';
import { getJobStatus, photoEditPreview, PhotoEditPreviewData } from 'src/api/functions/assetsApi';
import messages from '../messages';

const JOB_POLL_INTERVAL_MS = 5000;
const JOB_POLL_MAX_ATTEMPTS = 60; // ~5 minutes

export type PreviewRequest = {
  operation: string;
  params: { [key: string]: any };
};

type Options = {
  assetId: string;
  /**
   * Set by the caller when its panel unmounts. Checked between polls so a
   * closed panel stops polling and never alerts or stages into a dead tree.
   */
  cancelledRef: React.MutableRefObject<boolean>;
};

/**
 * Stage a photo-edit preview: submit, wait out an async job if the backend
 * returns one, and surface failures.
 *
 * Extracted from AIImageEditDialog because the cut-out panel needs the same
 * three-way response handling (inline / job / error) and the same 402
 * treatment. Returns the preview data, or `null` when the caller should stop
 * quietly — every failure path has already alerted the user by then, so there
 * is nothing for the caller to report.
 *
 * Deliberately does NOT own `applying` or the staged token: those belong to the
 * panel, which also has to discard the previous token before staging a new one.
 */
export function usePhotoEditPreview({ assetId, cancelledRef }: Options) {
  const { formatMessage } = useIntl();
  const accessToken = useAccessToken();
  const { showAlert, showInfo } = useAlerts();

  const pollJobForPreview = useCallback(
    async (jobId: string): Promise<PhotoEditPreviewData | null> => {
      for (let attempt = 0; attempt < JOB_POLL_MAX_ATTEMPTS; attempt++) {
        if (cancelledRef.current) return null;
        await new Promise((r) => setTimeout(r, JOB_POLL_INTERVAL_MS));
        const job: any = await getJobStatus(jobId, accessToken);
        const status = job?.status;
        if (status === 'COMPLETED') {
          return (job?.callback?.result as PhotoEditPreviewData) || null;
        }
        if (status === 'ERROR') {
          throw new Error(job?.message || formatMessage(messages.aiEditFailed));
        }
      }
      throw new Error(formatMessage(messages.aiEditTimedOut));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken, formatMessage],
  );

  const submitPreview = useCallback(
    async (body: PreviewRequest): Promise<PhotoEditPreviewData | null> => {
      const result = await photoEditPreview(assetId, body, accessToken);

      if (result.kind === 'error') {
        const err = result.error;
        if (err?.status === 402) {
          const data = err?.error?.data || {};
          showAlert(
            formatMessage(messages.aiEditInsufficientCredits, {
              required: data.required ?? '?',
              balance: data.balance ?? '?',
            }),
          );
        } else {
          showAlert(err?.error?.message || err?.message || formatMessage(messages.aiEditFailed));
        }
        return null;
      }

      let data: PhotoEditPreviewData | null = null;
      if (result.kind === 'inline') {
        data = result.data;
      } else if (result.kind === 'job') {
        showInfo(formatMessage(messages.aiEditProcessing));
        data = await pollJobForPreview(result.jobId);
      }

      if (cancelledRef.current) return null;
      if (!data || !data.preview_url) {
        showAlert(formatMessage(messages.aiEditFailed));
        return null;
      }
      if (typeof data.credits_charged === 'number' && data.credits_charged > 0) {
        showInfo(formatMessage(messages.aiEditCreditsCharged, { credits: data.credits_charged }));
      }
      return data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetId, accessToken, formatMessage, pollJobForPreview],
  );

  return { submitPreview };
}

export default usePhotoEditPreview;
